import { FileTextOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Folder, FolderUp } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/shadcn/ui/badge';
import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { studioQueryKeys } from '@/configs/react-query-keys/studio';
import type {
  AggregatedReportListItem,
} from '@/entities/report';
import { getReport, updateReportMutationOptions } from '@/entities/report';
import { SharedLinkDialog } from '@/entities/shared-link/ui/shared-link-dialog';
import { updateStudioItemTitleMutationOptions } from '@/entities/studio';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import { Button as DialogButton, Input, Label } from '@/shared/ui';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { getTimezoneDayjs } from '@/utils/time';

import { ReportActionMenu } from './report-action-menu';
import { ReportFolderActionMenu } from './report-folder-action-menu';
import type { ReportListRow } from './report-list-row';

type HookParams = { workspaceId: string };
type HookReturn = { columns: ColumnDef<ReportListRow>[] };

function ShareReportDialog({
  report,
  isOpen,
  close,
  unmount,
}: {
  report: AggregatedReportListItem;
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
}) {
  const workspaceId = useWorkspaceId();
  const targetType =
    report.source_type === 'HEY_SYNCLY' ? 'HEY_SYNCLY' : 'REPORT';
  const targetPath =
    targetType === 'HEY_SYNCLY'
      ? `/workspace/${workspaceId}/studio/${report.alias}`
      : `/workspace/${workspaceId}/report/${report.alias}`;

  return (
    <SharedLinkDialog
      title="Share report"
      description="Manage the shared link settings for this report."
      targetId={report.alias}
      targetType={targetType}
      targetLink={`${window.location.origin}${targetPath}`}
      isOpen={isOpen}
      close={close}
      unmount={unmount}
    />
  );
}

function RenameReportDialog({
  report,
  onConfirm,
  isOpen,
  close,
  unmount,
}: {
  report: AggregatedReportListItem;
  onConfirm: (name: string) => Promise<void>;
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
}) {
  const [draftName, setDraftName] = useState(report.title);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => setDraftName(report.title), [report.title]);

  const trimmedDraftName = draftName.trim();

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="s">
        <DialogHeader>
          <DialogTitle>Rename report</DialogTitle>
          <DialogDescription>
            Enter a new name for this report.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="pb-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="report-name">Report name</Label>
            <Input
              id="report-name"
              value={draftName}
              onChange={(event) => setDraftName(event.currentTarget.value)}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <div />
          <div className="flex gap-2">
            <DialogButton
              type="button"
              variant="outline"
              onClick={() => close()}
              disabled={isSubmitting}
            >
              Cancel
            </DialogButton>
            <DialogButton
              type="button"
              onClick={() => {
                void (async () => {
                  if (!trimmedDraftName || isSubmitting) return;

                  setIsSubmitting(true);
                  try {
                    await onConfirm(trimmedDraftName);
                    close();
                  } finally {
                    setIsSubmitting(false);
                  }
                })();
              }}
              disabled={!trimmedDraftName}
              isLoading={isSubmitting}
            >
              Rename
            </DialogButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const useReportTable = ({ workspaceId }: HookParams): HookReturn => {
  const members = useWorkspaceStore((state) => state.currWorkspaceMembers);
  const memberNameById = useMemo(
    () =>
      new Map(
        members.map((member) => [member.user_id, member.first_name] as const),
      ),
    [members],
  );
  const queryClient = useQueryClient();
  const updateReportMutation = useMutation(
    updateReportMutationOptions(queryClient),
  );
  const updateStudioItemTitleMutation = useMutation(
    updateStudioItemTitleMutationOptions(queryClient),
  );

  const openShareOverlay = (report: AggregatedReportListItem) => {
    overlay.open(({ isOpen, close, unmount }) => (
      <ShareReportDialog
        report={report}
        isOpen={isOpen}
        close={close}
        unmount={unmount}
      />
    ));
  };

  const openRenameOverlay = (report: AggregatedReportListItem) => {
    overlay.open(({ isOpen, close, unmount }) => (
      <RenameReportDialog
        report={report}
        isOpen={isOpen}
        close={close}
        unmount={unmount}
        onConfirm={async (name) => {
          if (report.source_type === 'HEY_SYNCLY') {
            await updateStudioItemTitleMutation.mutateAsync({
              item_id: report.alias,
              title: name,
            });
            await queryClient.invalidateQueries({
              queryKey: [studioQueryKeys.STUDIO_ITEM_DETAIL, report.alias],
            });
          } else {
            const currentReport = await getReport({ report_id: report.alias });
            await updateReportMutation.mutateAsync({
              report_id: report.alias,
              title: name,
              items: currentReport.items,
              rows: currentReport.rows,
            });
          }

          await queryClient.invalidateQueries({
            queryKey: ['report', report.alias],
          });
          toast('Report updated', 'success');
        }}
      />
    ));
  };

  const columns: ColumnDef<ReportListRow>[] = [
      {
        id: 'select',
        size: 44,
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all reports on this page"
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(checked) =>
              table.toggleAllPageRowsSelected(Boolean(checked))
            }
          />
        ),
        cell: ({ row }) =>
          row.original.kind === 'report' ? (
            <Checkbox
              aria-label={`Select ${row.original.report.title}`}
              checked={row.getIsSelected()}
              onCheckedChange={(checked) => row.toggleSelected(Boolean(checked))}
              onClick={(event) => event.stopPropagation()}
            />
          ) : null,
        meta: { className: 'px-3' },
      },
      {
        id: 'name',
        header: () => <span>Name</span>,
        cell: ({ row }) => {
          if (row.original.kind === 'parent') {
            return (
              <div className="flex min-w-0 items-center gap-2.5">
                <FolderUp className="size-4 shrink-0" />
                <span className="text-sm font-medium">..</span>
              </div>
            );
          }

          if (row.original.kind === 'folder') {
            return (
              <div className="flex min-w-0 items-center gap-2.5">
                <Folder className="size-4 shrink-0" />
                <span className="truncate text-sm font-medium">
                  {row.original.folder.name}
                </span>
              </div>
            );
          }

          const report = row.original.report;
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              <FileTextOutlined
                style={{
                  fontSize: 16,
                  flexShrink: 0,
                  color: 'var(--foreground)',
                }}
              />
              <span className="truncate text-sm font-medium text-foreground">
                {report.title}
              </span>
              {report.source_type === 'HEY_SYNCLY' ? (
                <Badge
                  variant="secondary"
                  className="h-[18px] shrink-0 px-1.5 py-0 text-[10px] font-semibold"
                >
                  AI
                </Badge>
              ) : null}
            </div>
          );
        },
        meta: { className: 'flex-1 min-w-0 px-2' },
      },
      {
        id: 'updated_at',
        header: () => <span>Last updated</span>,
        size: 140,
        cell: ({ row }) => {
          if (row.original.kind === 'parent') return null;
          const updatedAt =
            row.original.kind === 'folder'
              ? row.original.folder.updated_at
              : row.original.report.updated_at;
          return (
            <span className="text-sm text-muted-foreground">
              {getTimezoneDayjs(updatedAt).fromNow()}
            </span>
          );
        },
        meta: { className: 'px-4' },
      },
      {
        id: 'updated_by',
        header: () => <span>Modified by</span>,
        size: 140,
        cell: ({ row }) => {
          if (row.original.kind === 'parent') return null;
          const updatedBy =
            row.original.kind === 'folder'
              ? row.original.folder.updated_by
              : row.original.report.updated_by;
          return (
            <span className="text-sm text-muted-foreground">
              {memberNameById.get(updatedBy) || '-'}
            </span>
          );
        },
        meta: { className: 'px-4' },
      },
      {
        id: 'actions',
        header: () => <span />,
        size: 56,
        cell: ({ row }) => {
          if (row.original.kind === 'parent') return null;

          const original = row.original;
          if (original.kind === 'folder') {
            return (
              <ReportFolderActionMenu
                workspaceId={workspaceId}
                folder={original.folder}
              />
            );
          }

          return (
            <ReportActionMenu
              report={original.report}
              onRename={() => openRenameOverlay(original.report)}
              onShare={() => openShareOverlay(original.report)}
            />
          );
        },
      },
    ];

  return { columns };
};
